// Symbol/term banks for the Intermediate -> Advanced games. g = drawn glyph, t = text style.
const B = (id, sym, name, m, opt = {}) => ({ id, sym, name, m, ...opt });
const word = (w, m) => B(w, w, w, m, { t: "term" });

export const BANKS = {
  accidentals: [
    B("sharp", "♯", "Sharp", "raises a note by a semitone", { t: "acc" }),
    B("flat", "♭", "Flat", "lowers a note by a semitone", { t: "acc" }),
    B("natural", "♮", "Natural", "cancels a sharp or flat", { t: "acc" }),
    B("dsharp", "x", "Double sharp", "raises a note by a tone (two semitones)", { t: "acc" }),
    B("dflat", "♭♭", "Double flat", "lowers a note by a tone (two semitones)", { t: "acc" }),
  ],
  dynamics: [
    B("pp", "pp", "Pianissimo", "very quiet", { t: "dyn" }), B("p", "p", "Piano", "quiet", { t: "dyn" }),
    B("mp", "mp", "Mezzo piano", "moderately quiet", { t: "dyn" }), B("mf", "mf", "Mezzo forte", "moderately loud", { t: "dyn" }),
    B("f", "f", "Forte", "loud", { t: "dyn" }), B("ff", "ff", "Fortissimo", "very loud", { t: "dyn" }),
    B("sfz", "sfz", "Sforzando", "a sudden strong accent", { t: "dyn" }), B("fp", "fp", "Forte-piano", "loud then immediately quiet", { t: "dyn" }),
    B("cresc", "", "Crescendo", "gradually getting louder", { g: "cresc" }), B("dim", "", "Diminuendo", "gradually getting quieter", { g: "dim" }),
  ],
  articulation: [
    B("staccato", "", "Staccato", "short and detached", { g: "staccato" }),
    B("staccatissimo", "", "Staccatissimo", "very short and detached", { g: "wedge" }),
    B("accent", "", "Accent", "play this note louder (stressed)", { g: "accent" }),
    B("tenuto", "", "Tenuto", "hold for its full length, slightly leaned on", { g: "tenuto" }),
    B("marcato", "", "Marcato", "strongly accented and marked", { g: "marcato" }),
    B("fermata", "", "Pause (fermata)", "hold the note longer than its value", { g: "fermata" }),
    B("slur", "", "Slur", "play the notes smoothly joined together", { g: "slur" }),
  ],
  tempo: [
    word("Largo", "very slow and broad"), word("Adagio", "slow"), word("Andante", "at a walking pace"),
    word("Moderato", "at a moderate speed"), word("Allegro", "fast and lively"), word("Presto", "very fast"),
    word("rit.", "gradually getting slower"), word("accel.", "gradually getting faster"), word("a tempo", "return to the original speed"),
    word("rubato", "with some freedom of time"),
  ],
  terms: [
    word("legato", "smoothly"), word("cantabile", "in a singing style"), word("dolce", "sweetly"), word("con brio", "with vigour"),
    word("espressivo", "expressively"), word("sempre", "always"), word("poco a poco", "little by little"), word("molto", "very / much"),
    word("subito", "suddenly"), word("sostenuto", "sustained"), word("tutti", "everyone plays or sings"), word("simile", "carry on in the same way"),
  ],
  roadmap: [
    B("dc", "D.C.", "Da Capo", "go back to the beginning", { t: "term" }),
    B("ds", "D.S.", "Dal Segno", "go back to the sign", { t: "term" }),
    B("fine", "Fine", "Fine", "the end", { t: "term" }),
    B("segno", "", "Segno", "the sign that D.S. sends you back to", { g: "segno" }),
    B("coda", "", "Coda sign", "jump to the coda (ending section)", { g: "coda" }),
    B("repeat", "", "Repeat signs", "play the music between them twice", { g: "repeat" }),
    B("volta", "1.", "First-time bar", "play this bar the first time only", { t: "term" }),
    B("bar-repeat", "%", "Bar repeat", "play the previous bar again", { t: "acc" }),
  ],
  ornaments: [
    B("trill", "tr", "Trill", "rapidly alternate with the note above", { t: "dyn" }),
    word("acciaccatura", "a quick 'crushed' grace note"), word("appoggiatura", "a leaning grace note that takes time"),
    word("mordent", "one quick flick to a neighbouring note and back"), word("turn", "the note above, the note, the note below, the note"),
    word("glissando", "slide between two notes"),
  ],
  drums: [
    B("ghost", "( ● )", "Ghost note", "a very soft snare hit", { t: "acc" }),
    B("flam", "", "Flam", "a grace note just before the main stroke", { g: "flam" }),
    B("open-hh", "o", "Open hi-hat", "let the hi-hat open", { t: "acc" }),
    B("closed-hh", "+", "Closed hi-hat", "keep the hi-hat closed", { t: "acc" }),
    B("cymbal", "×", "X notehead", "a cymbal (hi-hat, ride or crash)", { t: "acc" }),
    B("paradiddle", "RLRR LRLL", "Paradiddle", "single, single, double sticking", { t: "term" }),
    B("buzz", "", "Buzz roll", "press the stick into the drum to buzz", { g: "buzz" }),
    B("sim", "sim.", "Simile", "keep playing the same groove", { t: "term" }),
  ],
  tab: [
    B("h", "H", "Hammer-on", "fret the next note without picking", { t: "acc" }),
    B("pull", "P", "Pull-off", "pull the finger off to sound a lower note", { t: "acc" }),
    B("slide", "5/7", "Slide", "slide up from one fret to another", { t: "acc" }),
    B("bend", "7b9", "Bend", "push the string to raise the pitch", { t: "acc" }),
    B("vib", "~", "Vibrato", "a gentle wobble in pitch", { t: "acc" }),
    B("pm", "P.M.", "Palm mute", "rest your picking hand on the strings", { t: "term" }),
    B("dead", "x", "Dead note", "a muted, percussive hit with no pitch", { t: "acc" }),
    B("harm", "<12>", "Natural harmonic", "touch the string lightly above the fret", { t: "acc" }),
  ],
  piano: [
    B("ped", "Ped.", "Sustain pedal", "press the right pedal down", { t: "dyn" }),
    B("pedup", "*", "Pedal release", "lift the sustain pedal", { t: "acc" }),
    word("una corda", "use the soft (left) pedal"), word("tre corde", "release the soft pedal"),
    B("8va", "8va", "Ottava", "play an octave higher than written", { t: "dyn" }),
    B("8vb", "8vb", "Ottava bassa", "play an octave lower than written", { t: "dyn" }),
    B("md", "m.d.", "Mano destra", "play with the right hand", { t: "term" }),
    B("ms", "m.s.", "Mano sinistra", "play with the left hand", { t: "term" }),
    B("f1", "1", "Finger 1", "the thumb", { t: "acc" }),
  ],
  voice: [
    B("breath", "’", "Breath mark", "take a breath here", { t: "acc" }),
    B("caesura", "//", "Caesura", "a short silent break", { t: "acc" }),
    word("a cappella", "sung with no accompaniment"), word("bocca chiusa", "with the mouth closed (humming)"),
    word("mezza voce", "with half voice"), word("sotto voce", "in an undertone, very softly"),
    word("divisi", "the part splits into two"), word("unis.", "everyone sings the same notes"),
    B("phrase", "", "Phrase mark", "sing these notes in one breath", { g: "phrase" }),
  ],
  time: [
    B("68", "6/8", "6/8", "two dotted-crotchet beats in a bar", { t: "dyn" }), B("98", "9/8", "9/8", "three dotted-crotchet beats in a bar", { t: "dyn" }),
    B("128", "12/8", "12/8", "four dotted-crotchet beats in a bar", { t: "dyn" }), B("34", "3/4", "3/4", "three crotchet beats in a bar", { t: "dyn" }),
    B("54", "5/4", "5/4", "five crotchet beats in a bar", { t: "dyn" }), B("78", "7/8", "7/8", "seven quavers in a bar", { t: "dyn" }),
    B("c", "C", "Common time", "the same as 4/4", { t: "dyn" }), B("cut", "¢", "Cut common time", "the same as 2/2", { t: "dyn" }),
  ],
  intervals: [
    word("minor 2nd", "1 semitone"), word("major 2nd", "2 semitones"), word("minor 3rd", "3 semitones"), word("major 3rd", "4 semitones"),
    word("perfect 4th", "5 semitones"), word("tritone", "6 semitones"), word("perfect 5th", "7 semitones"), word("octave", "12 semitones"),
  ],
  chords: [
    B("C", "C", "C", "C major triad", { t: "dyn" }), B("Cm", "Cm", "Cm", "C minor triad", { t: "dyn" }),
    B("Cdim", "C°", "C°", "C diminished triad", { t: "dyn" }), B("Caug", "C+", "C+", "C augmented triad", { t: "dyn" }),
    B("C7", "C7", "C7", "C dominant seventh", { t: "dyn" }), B("Cmaj7", "Cmaj7", "Cmaj7", "C major seventh", { t: "dyn" }),
    B("Cm7", "Cm7", "Cm7", "C minor seventh", { t: "dyn" }), B("CE", "C/E", "C/E", "C major with E in the bass", { t: "dyn" }),
  ],
  scales: [
    word("natural minor", "the notes of the relative major, starting on the 6th"), word("harmonic minor", "natural minor with a raised 7th"),
    word("melodic minor", "raised 6th and 7th going up"), word("Dorian", "minor with a raised 6th"), word("Mixolydian", "major with a flattened 7th"),
    word("Lydian", "major with a raised 4th"), word("pentatonic", "a five-note scale"), word("chromatic", "every semitone in order"),
  ],
  rhythm: [
    B("triplet", "3", "Triplet", "three notes in the time of two", { g: "triplet" }),
    word("swing", "long-short quavers, like a shuffle"), word("syncopation", "accents off the main beats"),
    word("semiquaver", "a quarter of a beat (in 4/4)"), word("dotted crotchet", "one and a half beats"),
    word("quintuplet", "five notes in the time of four"), word("hemiola", "two bars of 3 felt as three bars of 2"),
  ],
  notation: [
    B("tie", "", "Tie", "joins two notes of the same pitch: play once, hold for both", { g: "tie" }),
    B("slur2", "", "Slur", "a curve over different pitches: play them smoothly", { g: "slur" }),
    B("beamed", "", "Beamed quavers", "quavers joined in beat groups so the beats are easy to see", { g: "beam" }),
    word("simple time", "each beat divides into 2 (e.g. 2/4, 3/4, 4/4)"),
    word("compound time", "each beat divides into 3 (e.g. 6/8, 9/8, 12/8)"),
    word("anacrusis", "an upbeat: an incomplete bar before the first full bar"),
    word("duple", "2 beats in a bar"), word("triple", "3 beats in a bar"), word("quadruple", "4 beats in a bar"),
    B("dblbar", "", "Double barline", "marks the end of a section or piece", { g: "dblbar" }),
  ],
};

const COMMON_TITLES = {
  accidentals: "Accidentals", dynamics: "Dynamics", articulation: "Articulation", tempo: "Tempo words", terms: "Italian terms",
  roadmap: "Road map signs", ornaments: "Ornaments", drums: "Drum marks", tab: "TAB techniques", piano: "Pedal & hand marks",
  voice: "Breathing & phrasing", time: "Time signatures", intervals: "Intervals", chords: "Chord symbols", scales: "Scales & modes", rhythm: "Rhythm",
  notation: "Ties, slurs, beaming & time",
};
export const bankTitle = (k) => COMMON_TITLES[k];

// Lesson id -> banks for that lesson's games. The last lesson of each course mixes everything.
const PLAN = {
  11: ["time", "tempo"], 12: ["scales", "accidentals"], 13: ["intervals", "dynamics"], 14: ["chords", "articulation"], 15: ["chords", "terms"],
  16: ["rhythm", "tempo"], 17: ["scales", "voice"], 18: ["accidentals", "roadmap"], 19: ["ornaments", "articulation", "dynamics"],
  31: ["rhythm", "drums"], 32: ["rhythm", "dynamics"], 33: ["drums", "articulation"], 34: ["time", "tempo"], 35: ["drums", "dynamics"],
  36: ["rhythm", "terms"], 37: ["roadmap"], 38: ["drums", "tempo", "terms"], 39: ["drums", "articulation"],
  51: ["tab", "accidentals"], 52: ["chords", "dynamics"], 53: ["accidentals", "articulation"], 54: ["intervals", "tab"], 55: ["chords", "terms"],
  56: ["roadmap", "rhythm"], 57: ["accidentals", "scales"], 58: ["rhythm", "tempo"], 59: ["articulation", "dynamics", "terms"],
  71: ["chords", "piano"], 72: ["chords", "dynamics"], 73: ["piano", "accidentals"], 74: ["piano", "scales"], 75: ["chords", "roadmap"],
  76: ["rhythm", "tempo"], 77: ["articulation", "terms"], 78: ["scales", "accidentals"], 79: ["ornaments", "dynamics", "piano"],
};
const FINAL = { 20: [11, 19], 40: [31, 39], 60: [51, 59], 80: [71, 79] };
export const banksFor = (id) => {
  if (PLAN[id]) return [...PLAN[id], "notation"];
  const r = FINAL[id];
  if (!r) return null;
  const all = new Set();
  for (let i = r[0]; i <= r[1]; i++) PLAN[i].forEach((b) => all.add(b));
  return [...all, "notation"];
};
